import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { FooterButton } from '../../core/components';
import WebcamFileUploadModal from '../../core/components/modal/webcam-multi-capture-modal';
import ViewAllImagesModal from '../../core/components/viewallimagesmodal/view-all-images-modal';
import SegmentedImagesModal from '../../core/components/modal/segmentedimagesmodal/segmented-images-modal';
import { WaterTowersService } from '../../service/api/water-towers-service';
import { Table } from 'antd';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showToast } from '../../core/redux/toast/slice';
import { useDocumentUpload } from '../../core/hooks';

const WaterTowersDetailsForm = ({ data, isEditMode, cellActionRecord, onChanges, onCloseModal }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { uploadDocuments, loading } = useDocumentUpload();
  const [formData, setFormData] = useState({
    assetNumber: data?.assetNumber || '',
    date: new Date().toISOString().split('T')[0], // Initialize with current date
    status: '',
    brand: data?.brand || '',
    serviceDate: '',
    machineNumber: '',
    serialNumber: data?.serialNumber || '',
    location: '',
    cellLocation: data?.cellNumber || '',
    lane: data?.lane || '',
    modelNumber: data?.modelNumber || '',
    tonnage: data?.tonnage || '',
    servicedBy: '',
    version: '',
    totalCyclesSinceLastMaintenance: 0, // Initialize as number
    totalCyclesToDate: new Date().toISOString().split('T')[0], // Initialize as date string
    lastMaintenanceDate: new Date().toISOString().split('T')[0], // Initialize with current date
    maintenanceIntervalCycles: 0, // Initialize as number
    currentMaintenanceDate: new Date().toISOString().split('T')[0], // Initialize with current date
    notes: '',
    // Document Management fields
    serviceContactExpanded: false,
    serviceContact: '',
    oilUsedExpanded: false,
    oilUsed: '',
    serviceContractExpanded: false,
    serviceContractFile: null,
    // Serial Plate document fields
    serialPlateDocument: '',
    ...data
  });

  // Webcam modal state
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [showImageViewerModal, setShowImageViewerModal] = useState(false);
  const [showManageImagesModal, setShowManageImagesModal] = useState(false);
  const [serialPlateDocuments, setSerialPlateDocuments] = useState([]);
  const [waterTowersMaintenanceId, setWaterTowersMaintenanceId] = useState(0);
  
  // Document management state
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [tempFile, setTempFile] = useState(null);
  const [contractFiles, setContractFiles] = useState([]);
  const [processedImageSegments, setProcessedImageSegments] = useState([]);

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onChanges(updatedData);
  };

  const handleSerialPlateUpload = uploadedFiles => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const docs = uploadedFiles.map(file => ({
      name: file.fileName ?? 'Captured Image',
      path: file.filePath ?? file.relativePath ?? '',
    }));

    // Update modal viewer
    setSerialPlateDocuments(docs);

    // Save in form data (comma-separated for backend)
    setFormData(prev => ({
      ...prev,
      serialPlateDocument: docs.map(doc => doc.path).join(','),
    }));
  };

  const handleWaterTowersMaintenanceSaved = (maintenanceId) => {
    console.log('Water Towers maintenance saved with ID:', maintenanceId);
    setWaterTowersMaintenanceId(maintenanceId);
  };

  const handleEditClick = record => {
    console.log('handleEditClick called with record:', record);
    setSelectedRecord(record);

    if (
      record.documenttype === 'Service Contact' ||
      record.documenttype === 'Oil Used' ||
      record.documenttype === 'Service Contract'
    ) {
      setExpandedRowKeys([record.key]);
    } else {
      setExpandedRowKeys([]);
      console.log('About to click file input, fileInputRef.current:', fileInputRef.current);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleViewClick = record => {
    if (
      record.documenttype === 'Spec Sheet' ||
      record.documenttype === 'Serial Plate' ||
      record.documenttype === 'Service Manual' ||
      record.documenttype === 'Service Contract'
    ) {
      const fileUrl = getDocument(record);
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      } else {
        dispatch(
          showToast({
            message: 'Document not available',
            type: 'warning',
          }),
        );
      }
    }
  };

  const getDocumentName = path => {
    if (!path) return '';
    const pathParts = path.split('/');
    return pathParts[pathParts.length - 1] || path;
  };

  const getDocument = record => {
    if (record.documenttype == 'Spec Sheet') {
      return formData.specSheetDocument;
    } else if (record.documenttype == 'Serial Plate') {
      return formData.serialPlateDocument;
    } else if (record.documenttype == 'Service Manual') {
      return formData.serviceManualDocument;
    } else if (record.documenttype == 'Oil Used') {
      return formData.oilUsedDocument;
    } else {
      return '';
    }
  };

  const handleFileChange = async e => {
    console.log('handleFileChange called, selectedRecord:', selectedRecord);
    const file = e.target.files[0];
    console.log('Selected file:', file);
    
    if (file && selectedRecord) {
      console.log('Processing file upload for document type:', selectedRecord.documenttype);
      
      if (selectedRecord.documenttype === 'Service Contract') {
        setTempFile(file);
      } else {
        try {
          const docFormData = new FormData();
          docFormData.append('file', file);
          docFormData.append('fileCategoryId', file.documentName);
          docFormData.append('uploadCategory', `MACHINE`);
          console.log('Uploading file with FormData:', docFormData);
          
          const result = await uploadDocuments(docFormData);
          console.log('Upload result:', result);

          if (selectedRecord.documenttype == 'Spec Sheet') {
            setFormData({ ...formData, specSheetDocument: result.relativePath });
          } else if (selectedRecord.documenttype == 'Serial Plate') {
            setFormData({ ...formData, serialPlateDocument: result.relativePath });
          } else if (selectedRecord.documenttype == 'Service Manual') {
            setFormData({ ...formData, serviceManualDocument: result.relativePath });
          } else if (selectedRecord.documenttype == 'Oil Used') {
            setFormData({ ...formData, oilUsedDocument: result.relativePath });
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          dispatch(
            showToast({
              message: 'Error uploading file: ' + error.message,
              type: 'danger',
            }),
          );
        }
      }
    } else {
      console.log('No file or selectedRecord missing:', { file, selectedRecord });
    }
  };

  const handleAddContractFile = async () => {
    if (tempFile) {
      try {
        const docFormData = new FormData();
        docFormData.append('file', tempFile);
        docFormData.append('uploadCategory', 'MACHINE');
        const result = await uploadDocuments(docFormData);

        const newContractFile = {
          id: Date.now(),
          fileName: tempFile.name,
          filePath: result.relativePath,
          fileSize: tempFile.size,
          uploadDate: new Date().toISOString(),
        };

        setContractFiles(prev => [...prev, newContractFile]);
        setTempFile(null);

        dispatch(
          showToast({
            message: 'File uploaded successfully',
            type: 'success',
          }),
        );
      } catch (error) {
        console.error('Error uploading contract file:', error);
        dispatch(
          showToast({
            message: 'Error uploading file',
            type: 'danger',
          }),
        );
      }
    }
  };

  const handleRemoveContractFile = (fileId) => {
    setContractFiles(prev => prev.filter(file => file.id !== fileId));
  };

  // Document Management Table Columns
  const columns = [
    { title: 'Document Type', dataIndex: 'documenttype', key: 'documenttype' },
    { title: 'Format', dataIndex: 'format', key: 'format' },
    {
      title: 'Manage Images',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {record.documenttype === 'Serial Plate' ? (
              <Link
                className="me-2 p-2"
                disabled={!(serialPlateDocuments && serialPlateDocuments.length > 0) && !(processedImageSegments && processedImageSegments.length > 0)}
                onClick={() => {
                  setSelectedRecord(record);
                  setExpandedRowKeys([record.key]);
                  setShowManageImagesModal(true);
                }}
              >
                Manage Images
              </Link>
            ) : (
              <Link
                disabled={getDocument(record) == null}
                className="me-2 p-2"
                onClick={() => {
                  setSelectedRecord(record);
                  setExpandedRowKeys([record.key]);
                  handleViewClick(record);
                }}
              >
                View
              </Link>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {record.documenttype === 'Serial Plate' ? (
              <Link
                disabled={!isEditMode}
                className="me-2 p-2"
                style={{ minWidth: '140px', display: 'inline-block', textAlign: 'center' }}
                onClick={() => isEditMode && setShowWebcamModal(true)}
              >
                <i className="feather-camera me-1" /> Capture Image
              </Link>
            ) : (
              <Link
                disabled={!isEditMode}
                className="me-2 p-2"
                style={{ minWidth: '140px', display: 'inline-block', textAlign: 'center' }}
                onClick={() => isEditMode && handleEditClick(record)}
              >
                <i data-feather="edit" className="feather-edit"></i>
                {['Service Contact', 'Oil Used'].includes(record.documenttype)
                  ? 'Edit'
                  : 'Edit/Upload'}
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  const testdata = [
    {
      key: 1,
      documenttype: 'Spec Sheet',
      format: 'PDF',
    },
    {
      key: 2,
      documenttype: 'Serial Plate',
      format: 'Picture',
    },
    {
      key: 3,
      documenttype: 'Service Manual',
      format: 'PDF',
    },
    {
      key: 4,
      documenttype: 'Service Contact',
      format: 'List',
    },
    {
      key: 5,
      documenttype: 'Oil Used',
      format: 'List',
    },
    {
      key: 6,
      documenttype: 'Service Contract',
      format: 'PDF',
    },
  ];

  const handleSave = async () => {
    try {
    console.log('Saving Water Towers Maintenance Details:', formData);
      
      const payload = {
        id: 0,
        userId: localStorage.getItem('UserId'),
        assetNumber: formData.assetNumber,
        modelNumber: formData.modelNumber,
        serialNumber: formData.serialNumber,
        date: formData.date || null,
        location: formData.location,
        lastServicedBy: formData.lastServicedBy,
        lastServiceDate: formData.lastServiceDate || null,
        specSheetDocument: formData.specSheetDocument || '',
        serialPlateDocument: formData.serialPlateDocument || '',
        serviceManualDocument: formData.serviceManualDocument || '',
        serviceContactDocument: formData.serviceContactDocument || '',
        oilUsedDocument: formData.oilUsedDocument || '',
        notes: formData.notes || '',
        cellLocation: formData.cellLocation,
        waterTowerNumber: formData.machineNumber,
        waterTower: formData.assetNumber, // Add the required waterTower field
        totalCyclesSinceLastMaint: Number(formData.totalCyclesSinceLastMaintenance) || 0,
        totalCyclesToDate: formData.totalCyclesToDate || new Date().toISOString().split('T')[0],
        lastMaintenanceDate: formData.lastMaintenanceDate || null,
        maintenanceIntervalCycles: String(formData.maintenanceIntervalCycles || 0), // Convert to string
        currentMaintenanceDate: formData.currentMaintenanceDate || null,
        version: formData.version || '',
        status: formData.status || '',
        serviceContractDocuments: contractFiles
      };

      // Call the API endpoint using service
      const result = await WaterTowersService.saveWaterTowersMaintenance(payload);
      console.log('Water Towers maintenance saved successfully:', result);
      handleWaterTowersMaintenanceSaved(result.response.id);
    onCloseModal();
    } catch (error) {
      console.error('Error saving water towers maintenance:', error);
    }
  };

  const handleCancel = () => {
    onCloseModal();
  };

  return (
    <div className="container-fluid">
      {/* Water Towers Details Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Water Towers Details</h6>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Asset Number <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.assetNumber}
                onChange={e => handleInputChange('assetNumber', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Date <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="date"
                className="form-control"
                value={formData.date}
                onChange={e => handleInputChange('date', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Status</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.status}
                onChange={e => handleInputChange('status', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Brand <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.brand}
                onChange={e => handleInputChange('brand', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Service Date <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="date"
                className="form-control"
                value={formData.serviceDate}
                onChange={e => handleInputChange('serviceDate', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Water Towers Number <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.machineNumber}
                onChange={e => handleInputChange('machineNumber', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Serial Number <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.serialNumber}
                onChange={e => handleInputChange('serialNumber', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Location <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.location}
                onChange={e => handleInputChange('location', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Cell Location</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.cellLocation}
                onChange={e => handleInputChange('cellLocation', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Lane</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.lane}
                onChange={e => handleInputChange('lane', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Model Number <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.modelNumber}
                onChange={e => handleInputChange('modelNumber', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>
              Tonnage <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.tonnage}
                onChange={e => handleInputChange('tonnage', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Serviced By</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.servicedBy}
                onChange={e => handleInputChange('servicedBy', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Version</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.version}
                onChange={e => handleInputChange('version', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Usage Information Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Usage Information</h6>
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Total Cycles Since Last Maintenance</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.totalCyclesSinceLastMaintenance}
                onChange={e => handleInputChange('totalCyclesSinceLastMaintenance', e.target.value)}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Total Cycles to Date</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.totalCyclesToDate}
                onChange={e => handleInputChange('totalCyclesToDate', e.target.value)}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Last Maintenance Date</label>
            <div className="input-groupicon calender-input">
              <input
                type="date"
                className="form-control"
                value={formData.lastMaintenanceDate}
                onChange={e => handleInputChange('lastMaintenanceDate', e.target.value)}
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Maintenance Interval Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Maintenance Interval</h6>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Maintenance Interval (Cycles)</label>
            <div className="input-groupicon calender-input">
              <input
                type="text"
                className="form-control"
                value={formData.maintenanceIntervalCycles}
                onChange={e => handleInputChange('maintenanceIntervalCycles', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks mb-3">
            <label>Current Maintenance Date</label>
            <div className="input-groupicon calender-input">
              <input
                type="date"
                className="form-control"
                value={formData.currentMaintenanceDate}
                onChange={e => handleInputChange('currentMaintenanceDate', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Document Management Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Document Management</h6>
          <Table
            columns={columns}
            dataSource={testdata}
            pagination={false}
            size="small"
            bordered
            expandable={{
              expandedRowKeys: expandedRowKeys,
              onExpandedRowsChange: setExpandedRowKeys,
              expandedRowRender: record => {
                if (record.documenttype === 'Service Contact') {
                  return (
                    <div className="p-3 border rounded bg-light">
              <h6>Service Contact Details</h6>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Enter service contact details"
                value={formData.serviceContact || ''}
                onChange={e => handleInputChange('serviceContact', e.target.value)}
              />
            </div>
                  );
                } else if (record.documenttype === 'Oil Used') {
                  return (
                    <div className="p-3 border rounded bg-light">
              <h6>Oil Used Details</h6>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Enter oil used details"
                value={formData.oilUsed || ''}
                onChange={e => handleInputChange('oilUsed', e.target.value)}
              />
            </div>
                  );
                } else if (record.documenttype === 'Service Contract') {
                  return (
                    <div className="p-3 border rounded bg-light">
              <h6>Service Contract Upload</h6>
                      <div className="d-flex align-items-center mb-2">
                <input
                  type="file"
                  className="form-control me-2"
                  accept=".pdf"
                          onChange={e => setTempFile(e.target.files[0])}
                        />
                        <button 
                          className="btn btn-primary"
                          onClick={handleAddContractFile}
                          disabled={!tempFile}
                        >
                          Upload
                        </button>
              </div>
                      {tempFile && (
                        <div className="mb-2">
                          <small className="text-muted">Selected: {tempFile.name}</small>
                </div>
              )}
                      {contractFiles.length > 0 && (
                        <div>
                          <h6>Uploaded Files:</h6>
                          {contractFiles.map(file => (
                            <div key={file.id} className="d-flex justify-content-between align-items-center border p-2 mb-1">
                              <span>
                                <small>{getDocumentName(file.filePath)}</small>
                              </span>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveContractFile(file.id)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
            </div>
          )}
        </div>
                  );
                }
                return null;
              },
            }}
          />
        </div>
      </div>

      {/* Hidden file input for document uploads */}
      <div style={{ display: 'none' }}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileChange}
        />
      </div>

      {/* Notes Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Notes</h6>
          <div className="input-blocks">
            <textarea
              className="form-control"
              rows="5"
              value={formData.notes}
              onChange={e => handleInputChange('notes', e.target.value)}
              placeholder="Enter maintenance notes here..."
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="row">
        <div className="col-12 col-lg-8"></div>
        <div className="col-12 col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Save Water Towers Maintenance"
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
      </div>

      {/* Webcam Modal */}
      {showWebcamModal && (
        <WebcamFileUploadModal
          show={showWebcamModal}
          onClose={() => setShowWebcamModal(false)}
          onSubmit={handleSerialPlateUpload}
          context="water-towers"
          buttonLabel="Serial Plate"
          maintenanceData={formData}
          onMaintenanceSaved={handleWaterTowersMaintenanceSaved}
        />
      )}

      {/* Image Viewer Modal */}
      {showImageViewerModal && (
        <ViewAllImagesModal
          show={showImageViewerModal}
          onClose={() => setShowImageViewerModal(false)}
          documents={serialPlateDocuments}
        />
      )}

      {/* Manage Images Modal */}
      {showManageImagesModal && (
        <SegmentedImagesModal
          show={showManageImagesModal}
          onClose={() => setShowManageImagesModal(false)}
          documents={serialPlateDocuments}
          moldInfoID={0}
          buttonLabel="Serial Plate"
          maintenanceId={waterTowersMaintenanceId}
          context="water-towers"
        />
      )}
    </div>
  );
};

WaterTowersDetailsForm.propTypes = {
  data: PropTypes.object,
  isEditMode: PropTypes.bool,
  cellActionRecord: PropTypes.object,
  onChanges: PropTypes.func,
  onCloseModal: PropTypes.func,
};

export default WaterTowersDetailsForm;